import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createVNode as _createVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "navbar__content-area" }
const _hoisted_2 = { class: "navbar__actions d-flex align-center" }
const _hoisted_3 = { class: "mr-1" }
const _hoisted_4 = { class: "text-body-2" }
const _hoisted_5 = { class: "mr-2" }
const _hoisted_6 = { class: "navbar__user-menu d-flex align-center" }
const _hoisted_7 = { class: "initial-text" }

import { ref, computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useRoute, useRouter } from 'vue-router';
    import { useDisplay, useLocale } from 'vuetify';
    import { useStore } from 'vuex';
    import BaseSidebar from './BaseSidebar.vue';
    import type { NavItem } from '@/interfaces/navigation/NavItem.types';
    import { Colors } from '@/public/styles/utils/variables';
    import { LOCALES } from '@/locales/i18n';
    import AQModal from '@/components/shared/containers/AQModal.vue';
    import AQTextField from '@/components/shared/forms/inputs/AQTextField.vue';
    import AQTextArea from '@/components/shared/forms/inputs/AQTextArea.vue';
    import $rq from '@/api/api';
    import { toast } from 'vue3-toastify';
    import { phoneCountries } from '@/aquassist-shared/constants/phoneCountries';
    import { regexes } from '@/utilities/utils';
    import services from '@/routes/services';
    import laboratory from '@/routes/laboratory';
    import crm from '@/routes/crm';
    import administration from '@/routes/administration';
    import { getNavItemMeta } from '@/store/modules/navigation';
    import AQButton from '../shared/forms/inputs/AQButton.vue';
    import AQIcon from '../shared/interactions/AQIcon.vue';
    import { roles } from '@/constants/roles';

    interface NavbarProps {
        showNotifications?: boolean;
    }
    type SubmenuItem = {
        id: string;
        text: string;
        icon?: string;
        action: () => void;
        isExternal?: boolean;
    };

    type UserMenuItem =
        | {
              id: string;
              isHeader: true;
              dynamicText: () => string;
              text?: never;
              action?: never;
              isDivider: boolean;
              disabled?: boolean;
              icon?: false;
              hasSubmenu?: never;
          }
        | {
              id: string;
              isHeader?: false | undefined;
              text: string;
              action: (() => void) | (() => Promise<void>) | string;
              dynamicText?: never;
              isDivider: boolean;
              disabled?: boolean;
              icon?: string;
              hasSubmenu?: never;
          }
        | {
              id: string;
              isDivider: true;
              text?: never;
              dynamicText?: never;
              action?: never;
              isHeader: false;
              disabled: false;
              icon?: never;
              hasSubmenu?: never;
          }
        | {
              id: string;
              isHeader?: false;
              text: string;
              hasSubmenu: true;
              submenuItems: SubmenuItem[];
              dynamicText?: never;
              isDivider: false;
              disabled?: boolean;
              icon?: string;
              action?: never;
          };

    interface Language {
        code: string;
        title: string;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'Navbar',
  props: {
    showNotifications: { type: Boolean, default: true }
  },
  setup(__props: any) {

    const props = __props;

    const { locale, t } = useI18n();
    const display = useDisplay();
    const { current: vuetifyLocale } = useLocale();
    const route = useRoute();
    const store = useStore();
    const router = useRouter();
    const hasPermission = computed(() => store.getters.hasPermission);

    const currentUser = computed(() => store.getters['getCurrentUser']);
    const currentTeamName = computed<string | null>(() => store.getters['currentTeamName'] || null);
    const currentUserRole = computed(() => {
        const user = currentUser.value;
        if (!user || !Array.isArray(user.roles) || !user.roles[0]) {
            return { key: 'guest' };
        }
        return user?.roles?.[0];
    });
    const helpCenterSubmenuItems: SubmenuItem[] = [
        {
            id: 'webinar',
            text: t('helpCenter.webinar'),
            icon: 'mdi-video-outline',
            isExternal: false,
            action: () => {
                router.push({ name: 'movies' });
            },
        },
        {
            id: 'devices-documentation',
            text: t('helpCenter.devicesDocumentation'),
            icon: 'mdi-file-document-outline',
            isExternal: false,
            action: () => {
                router.push({ name: 'devicesDocumentation' });
            },
        },
        {
            id: 'aq-community',
            text: t('helpCenter.community'),
            icon: 'mdi-web',
            isExternal: true,
            action: () => {
                window.open('https://community.aquassist.com/', '_blank', 'noopener');
            },
        },
    ];

    if (
        store.getters.hasRole(roles.MANAGER.value) ||
        store.getters.hasRole(roles.ADMINISTRATOR.value)
    ) {
        helpCenterSubmenuItems.push({
            id: 'aquassist-support',
            text: t('helpCenter.support'),
            icon: 'mdi-headset',
            isExternal: true,
            action: () => {
                window.open(
                    'https://aquassist.atlassian.net/servicedesk/customer/portals',
                    '_blank',
                    'noopener',
                );
            },
        });
    }

    const userMenuItems = computed<UserMenuItem[]>(() => [
        {
            id: 'user-email',
            dynamicText: () => String(userEmail.value ?? ''),
            isHeader: true,
            isDivider: false,
            disabled: false,
        },
        {
            id: 'divider-after-email',
            isDivider: true,
            isHeader: false,
            disabled: false,
        },
        {
            id: 'profile',
            text: t('profile.title'),
            icon: 'mdi-account-outline',
            action: () => {
                router.push({ name: 'profile' });
            },
            isDivider: false,
            disabled: false,
        },
        {
            id: 'switch-team',
            text: t('teams.switchTeam'),
            icon: 'mdi-account-group-outline',
            action: () => {
                router.push({ name: 'teams' });
            },
            isDivider: false,
            disabled: !currentTeamName.value,
        },
        {
            id: 'divider-after-switch-team',
            isDivider: true,
            isHeader: false,
            disabled: false,
        },
        {
            id: 'release-notes',
            text: t('administration.release'),
            icon: 'mdi-notebook-outline',
            action: () => {
                //router.push({ name: 'ReleaseNotes' });
            },
            isDivider: false,
            disabled: false,
        },
        {
            id: 'divider-after-release-notes',
            isDivider: true,
            isHeader: false,
            disabled: false,
        },
        {
            id: 'send-feedback',
            text: t('feedback.title'),
            icon: 'mdi-message-text-outline',
            action: () => {
                isFeedbackModalOpen.value = true;
            },
            isDivider: false,
            disabled: false,
        },
        {
            id: 'help-center',
            text: t('helpCenter.title'),
            icon: 'mdi-help-circle-outline',
            hasSubmenu: true,
            isDivider: false,
            disabled: false,
            submenuItems: helpCenterSubmenuItems,
        },
        {
            id: 'divider-before-logout',
            isDivider: true,
            isHeader: false,
            disabled: false,
        },
        {
            id: 'logout',
            text: t('logout.pageName'),
            icon: 'mdi-logout-variant',
            action: async () => {
                await store.dispatch('logout');
                router.push('/');
            },
            isDivider: false,
            disabled: false,
        },
    ]);

    const drawerOpen = ref(false);
    const showBurgerMenu = computed(() => {
        // Burger icon is only for small screens
        return !display.lgAndUp.value;
    });

    const sidebarBehaviorForLargeScreen = computed<'expanded' | 'rail'>(
        () => store.getters['navigation/getSidebarBehaviorForLargeScreen'],
    );

    const isEnvironmentBarActive = ref(true);
    const environmentBarText = ref('');
    const environmentBarColorClass = ref('');

    const appMode = process.env.VUE_APP_MODE;

    if (appMode === 'demo') {
        environmentBarText.value = 'DEMO';
        environmentBarColorClass.value = 'environment-bar--demo';
    } else if (appMode === 'qa') {
        environmentBarText.value = 'QA';
        environmentBarColorClass.value = 'environment-bar--qa';
    } else if (appMode === 'dev') {
        environmentBarText.value = 'DEV';
        environmentBarColorClass.value = 'environment-bar--dev';
    } else {
        if (process.env.NODE_ENV === 'development') {
            environmentBarText.value = 'DEV';
            environmentBarColorClass.value = 'environment-bar--dev';
        } else {
            isEnvironmentBarActive.value = false;
        }
    }

    const computedEnvironmentBarColor = computed(() => {
        switch (environmentBarColorClass.value) {
            case 'environment-bar--dev':
                return Colors.environmentBarDev;
            case 'environment-bar--demo':
                return Colors.environmentBarDemo;
            case 'environment-bar--qa':
                return Colors.environmentBarQa;
            default:
                return 'none';
        }
    });

    const activeTab = computed<string | null>(() => {
        if (typeof route.path !== 'string') {
            return null;
        }
        const currentPath = route.path;
        // Client site detail routes (clientId + siteId params) should highlight the "Sites" tab
        const { clientId, siteId } = route.params;
        if (clientId && siteId) {
            const sitesItem = mainNavItems.value.find(
                (item) => item.name === 'sites' || item.path === '/sites',
            );
            return sitesItem?.name ?? null;
        }
        const pathSegments = currentPath.split('/');

        if (pathSegments.length < 2 || pathSegments[1] === '') {
            const rootItem = mainNavItems.value.find((item) => item.path === '/');
            return rootItem?.name ?? null;
        }

        const basePathForTabMatching = '/' + pathSegments[1];
        const foundItem = mainNavItems.value.find((item) => item.path === basePathForTabMatching);
        return foundItem?.name ?? null;
    });

    //ANY TO COMBINE BOTH NAVBAR AND SIDEBAR NEEDED
    //eslint-disable-next-line
    const mainNavItemsForSidebar = computed(() => mainNavItems.value as any);

    //eslint-disable-next-line
    const desktopDrawerNavItemsForSidebar = computed(() => desktopDrawerNavItems.value as any);

    const clientMainNavItems = computed<NavItem[]>(() => {
        const itemsFromStore = store.getters['navigation/getClientNavItems'] || [];
        const activeSubItems = itemsFromStore.map((item: NavItem) => {
            const filteredSubItems =
                item.subItems?.filter(
                    (subItem: NavItem) =>
                        subItem.allowedRoles?.includes('all') ||
                        subItem.allowedRoles?.includes(currentUserRole.value?.key),
                ) || [];
            return {
                ...item,
                subItems: filteredSubItems,
                title: t(`${item.name?.toLowerCase()}.pageName`),
            };
        });
        return activeSubItems;
    });

    const clientSiteMainNavItems = computed<NavItem[]>(() => {
        const itemsFromStore = store.getters['navigation/getClientSiteNavItems'] || [];
        // clientSite nav items should be filtered based on the role and just subItems should be considered
        const ss = itemsFromStore.map((item: NavItem) => {
            const filteredSubItems =
                item.subItems?.filter((subItem: NavItem) => {
                    const roleAllowed =
                        subItem.allowedRoles?.includes('all') ||
                        subItem.allowedRoles?.includes(currentUserRole.value.key);
                    if (!roleAllowed) return false;

                    // Cacher Devices menu  si le user n'a pas la permission de voir les devices
                    if (subItem.name === 'siteDevices') {
                        const canViewDevices = hasPermission.value('sites.devices.view');
                        return canViewDevices;
                    }
                    return true;
                }) || [];
            return {
                ...item,
                title: t(`${item.name?.toLowerCase()}.pageName`),
                subItems: filteredSubItems,
            };
        });
        return ss;
    });

    const desktopDrawerNavItems = computed<NavItem[]>(() => {
        if (!activeTab.value || !mainNavItems.value) {
            return [];
        }
        const currentPath = route.path;
        // Hide sidebar for notifications page
        if (currentPath === '/notifications') {
            return [];
        }
        // Special case: client site routes should have a special sidebar
        const { clientId, siteId } = route.params;
        if (regexes.clientSite.test(currentPath) && clientId && siteId) {
            // Create special sidebar for client site routes
            return clientSiteMainNavItems.value;
        }
        if (regexes.client.test(currentPath) && clientId) {
            // Create special sidebar for client routes
            return clientMainNavItems.value;
        }
        return mainNavItems.value || [];
    });

    const fieldServiceMainNavItems = computed<NavItem[]>(() => {
        return getNavItemMeta(services.children || [], services.path);
    });
    const laboratoryMainNavItems = computed<NavItem[]>(() => {
        return getNavItemMeta(laboratory.children || [], laboratory.path);
    });
    const adminMainNavItems = computed<NavItem[]>(() => {
        return getNavItemMeta(administration.children || [], administration.path);
    });
    const crmMainNavItems = computed<NavItem[]>(() => {
        return getNavItemMeta(crm.children || [], crm.path);
    });

    const featureFlagGetters: Record<string, string> = {
        EVENT_MANAGEMENT: 'showEventManagementByFeature',
        CONTRACT: 'showContractByFeature',
        GLOBAL_DASHBOARD: 'showGlobalDashboardByFeature',
        CRM: 'showCrmByFeature',
    };

    const isFeatureActive = (featureFlag?: string): boolean => {
        if (!featureFlag) return true;
        const getter = featureFlagGetters[featureFlag];
        if (!getter) return true;
        return !!store.getters[getter];
    };

    const isRoleAllowed = (item: NavItem): boolean => {
        if (item.superAdmin) return store.getters.superAdmin === true;
        return (
            !!item.allowedRoles?.includes('all') ||
            !!item.allowedRoles?.includes(currentUserRole.value?.key)
        );
    };

    const mainNavItems = computed<NavItem[]>(() => {
        const itemsFromStore = store.getters['navigation/getMainNavItems'] || [];
        if (!currentTeamName.value) return [];

        const items: NavItem[] = itemsFromStore
            .filter((item: NavItem) => {
                // If the feature flag is inactive, hide the item
                if (!isFeatureActive(item.featureFlag)) return false;
                // If item.allowedRoles contains 'all', include the item
                if (item.allowedRoles?.includes('all')) return true;
                // If item.allowedRoles contains the current role, include the item
                if (item.allowedRoles?.includes(currentUserRole.value?.key)) return true;
                // If any subItem is allowed, include the item
                if (item.subItems?.some(isRoleAllowed)) {
                    return true;
                }
                // Otherwise, exclude the item
                return false;
            })
            .map((item: NavItem) => {
                // Filter subItems
                // Recursively filter subItems
                function filterSubItems(subItems: NavItem[]): NavItem[] {
                    return (
                        subItems
                            ?.filter(
                                (subItem: NavItem) =>
                                    isFeatureActive(subItem.featureFlag) &&
                                    (isRoleAllowed(subItem) ||
                                        (subItem.subItems && subItem.subItems.some(isRoleAllowed))),
                            )
                            .map((subItem: NavItem) => ({
                                ...subItem,
                                subItems: subItem.subItems ? filterSubItems(subItem.subItems) : [],
                            })) || []
                    );
                }
                const filteredSubItems = item.subItems ? filterSubItems(item.subItems) : [];

                return {
                    ...item,
                    title: t(`${item.name?.toLowerCase()}.pageName`),
                    subItems: filteredSubItems,
                };
            });
        const currentPath = route.path;
        const { clientId, siteId } = route.params;
        if (regexes.clientSite.test(currentPath) && clientId && siteId && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('sitePage.pageName'),
                isDivider: true,
            };
            return [...items, divider, ...clientSiteMainNavItems.value];
        } else if (regexes.client.test(currentPath) && clientId && !display.lgAndUp.value) {
            // Create special sidebar for client routes
            const divider: NavItem = {
                title: t('clientPage.pageName'),
                isDivider: true,
            };
            return [...items, divider, ...clientMainNavItems.value];
        } else if (regexes.fieldService.test(currentPath) && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('servicesPage.services'),
                isDivider: true,
            };
            const _items = items.map((item) => {
                return {
                    ...item,
                    hasChildren: false,
                    subItems: [],
                };
            });
            return [..._items, divider, ...fieldServiceMainNavItems.value];
        } else if (regexes.laboratory.test(currentPath) && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('pageToDisplay.laboratory'),
                isDivider: true,
            };
            const _items = items.map((item) => {
                return {
                    ...item,
                    hasChildren: false,
                    subItems: [],
                };
            });
            return [..._items, divider, ...laboratoryMainNavItems.value];
        } else if (regexes.crm.test(currentPath) && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('crm.pageName'),
                isDivider: true,
            };
            const _items = items.map((item) => {
                return {
                    ...item,
                    hasChildren: false,
                    subItems: [],
                };
            });
            return [..._items, divider, ...crmMainNavItems.value];
        } else if (regexes.administration.test(currentPath) && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('administration.pageName'),
                isDivider: true,
            };
            const _items = items.map((item) => {
                return {
                    ...item,
                    hasChildren: false,
                    subItems: [],
                };
            });
            return [..._items, divider, ...adminMainNavItems.value];
        }
        return items;
    });

    const userEmail = computed<string | null>(() => store.state.auth.currentUser?.email || null);
    const clientInitial = computed(() =>
        currentTeamName.value ? currentTeamName.value[0].toUpperCase() : '',
    );

    const languageMenuOpen = ref(false);

    const availableLanguages = computed<Language[]>(() =>
        LOCALES.map((langCode) => ({
            code: langCode,
            title: langCode.charAt(0).toUpperCase() + langCode.slice(1),
        })),
    );

    const currentLanguage = computed<Language>(() => {
        return (
            availableLanguages.value.find((lang) => lang.code === store.getters.userLang) ||
            availableLanguages.value[0]
        );
    });

    const selectLanguage = (lang: Language) => {
        store.dispatch('setLang', lang.code);
        locale.value = lang.code;
        vuetifyLocale.value = lang.code;
        languageMenuOpen.value = false;
    };
    const userMenuOpen = ref(false);

    const unacknowledgedCount = computed(() => store.getters['notifications/unacknowledgedCount']);

    const goToNotifications = () => {
        router.push({ name: 'notifications' });
    };

    const handleSubmenuItemClick = (subItem: SubmenuItem) => {
        subItem.action();
        userMenuOpen.value = false;
    };

    const handleUserMenuItemClick = (actionProperty?: (() => void) | string) => {
        if (typeof actionProperty === 'function') {
            actionProperty();
        } else {
            if (typeof actionProperty === 'string') {
                router.push(actionProperty);
            }
        }
    };

    const isFeedbackModalOpen = ref(false);
    const feedbackInput = ref('');
    const feedbackMessage = ref('');

    const isConfirmDisabled = computed(() => !feedbackInput.value || !feedbackMessage.value);

    const handleConfirmModal = async (): Promise<void> => {
        const bodyParams = {
            title: feedbackInput.value,
            message: feedbackMessage.value,
            url: window.location.href,
        };

        await $rq.sendFeedBack(bodyParams);
        feedbackInput.value = '';
        feedbackMessage.value = '';
        toast.success(t('feedback.success'));
    };

    const getLanguageFlag = (langCode: string): string => {
        const specialCases: Record<string, string> = {
            en: 'GB',
        };

        const countryCode = specialCases[langCode] || langCode.toUpperCase();
        const country = phoneCountries.find((c) => c.iso2 === countryCode);
        return country?.flag || '🌐';
    };

return (_ctx: any,_cache: any) => {
  const _component_v_system_bar = _resolveComponent("v-system-bar")!
  const _component_v_app_bar_nav_icon = _resolveComponent("v-app-bar-nav-icon")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_list_subheader = _resolveComponent("v-list-subheader")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_badge = _resolveComponent("v-badge")!
  const _component_v_app_bar = _resolveComponent("v-app-bar")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isEnvironmentBarActive.value)
      ? (_openBlock(), _createBlock(_component_v_system_bar, {
          key: 0,
          app: "",
          color: computedEnvironmentBarColor.value,
          height: "32",
          style: {"justify-content":"center","color":"white","font-weight":"bold","text-transform":"uppercase"}
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(environmentBarText.value) + " --- " + _toDisplayString(currentUserRole.value.name) + " --- " + _toDisplayString(currentUser.value.firstName) + " " + _toDisplayString(currentUser.value.lastName) + " --- " + _toDisplayString(currentUser.value.email), 1)
          ]),
          _: 1
        }, 8, ["color"]))
      : _createCommentVNode("", true),
    (!_unref(display).lgAndUp.value)
      ? (_openBlock(), _createBlock(BaseSidebar, {
          key: 1,
          modelValue: drawerOpen.value,
          "onUpdate:modelValue": [
            _cache[0] || (_cache[0] = ($event: any) => ((drawerOpen).value = $event)),
            _cache[1] || (_cache[1] = ($event: any) => (drawerOpen.value = $event))
          ],
          temporary: "",
          floating: "",
          app: "",
          location: "left",
          width: "256",
          class: "mobile-nav-drawer",
          rail: false,
          items: mainNavItemsForSidebar.value,
          "disable-route-watcher": true
        }, null, 8, ["modelValue", "items"]))
      : _createCommentVNode("", true),
    (_unref(display).lgAndUp.value)
      ? (_openBlock(), _createBlock(BaseSidebar, {
          key: 2,
          "model-value": true,
          rail: sidebarBehaviorForLargeScreen.value === 'rail',
          items: desktopDrawerNavItemsForSidebar.value,
          permanent: "",
          app: "",
          location: "left",
          width: sidebarBehaviorForLargeScreen.value === 'rail' ? 72 : 256,
          "expand-on-hover": sidebarBehaviorForLargeScreen.value === 'rail',
          class: "main-app-sidebar"
        }, null, 8, ["rail", "items", "width", "expand-on-hover"]))
      : _createCommentVNode("", true),
    _createVNode(_component_v_app_bar, {
      app: "",
      flat: "",
      class: "navbar"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (showBurgerMenu.value)
            ? (_openBlock(), _createBlock(_component_v_app_bar_nav_icon, {
                key: 0,
                onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (drawerOpen.value = !drawerOpen.value), ["stop"])),
                class: "mr-2"
              }))
            : _createCommentVNode("", true),
          (_unref(display).lgAndUp.value)
            ? (_openBlock(), _createBlock(_component_v_tabs, {
                key: 1,
                "model-value": activeTab.value,
                "align-tabs": "start",
                class: "navbar__tabs",
                "slider-color": "transparent",
                height: "100%"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mainNavItems.value, (item) => {
                    return (_openBlock(), _createBlock(_component_v_tab, {
                      key: item.name,
                      value: item.name,
                      to: item.path,
                      class: _normalizeClass(["navbar__tab", { 'navbar__tab--active': activeTab.value === item.name }])
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(item.title), 1)
                      ]),
                      _: 2
                    }, 1032, ["value", "to", "class"]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["model-value"]))
            : _createCommentVNode("", true),
          _createVNode(_component_v_spacer),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_v_menu, {
              modelValue: languageMenuOpen.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((languageMenuOpen).value = $event)),
              "close-on-content-click": true,
              location: "bottom end",
              "offset-y": "",
              "content-class": "aq-menu-content"
            }, {
              activator: _withCtx(({ props: langProps }) => [
                _createElementVNode("div", _mergeProps(langProps, {
                  class: "navbar__language-pill d-flex align-center",
                  style: {"cursor":"pointer"}
                }), [
                  _createElementVNode("span", _hoisted_3, _toDisplayString(getLanguageFlag(currentLanguage.value.code)), 1),
                  _createElementVNode("span", _hoisted_4, _toDisplayString(currentLanguage.value.title), 1)
                ], 16)
              ]),
              default: _withCtx(() => [
                _createVNode(_component_v_list, { dense: "" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_subheader, null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('forms.labels.lang')), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_divider),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableLanguages.value, (lang) => {
                      return (_openBlock(), _createBlock(_component_v_list_item, {
                        key: lang.code,
                        onClick: ($event: any) => (selectLanguage(lang)),
                        active: lang.code === currentLanguage.value.code
                      }, _createSlots({
                        prepend: _withCtx(() => [
                          _createElementVNode("span", _hoisted_5, _toDisplayString(getLanguageFlag(lang.code)), 1)
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(lang.title), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, [
                        (lang.code === currentLanguage.value.code)
                          ? {
                              name: "append",
                              fn: _withCtx(() => [
                                _createVNode(AQIcon, {
                                  name: "mdi-check",
                                  size: "small"
                                })
                              ]),
                              key: "0"
                            }
                          : undefined
                      ]), 1032, ["onClick", "active"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue"]),
            (props.showNotifications)
              ? (_openBlock(), _createBlock(_component_v_badge, {
                  key: 0,
                  content: unacknowledgedCount.value,
                  "model-value": unacknowledgedCount.value > 0,
                  color: "error",
                  dot: "",
                  overlap: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(AQButton, {
                      class: "navbar__notification-icon",
                      variant: "text",
                      onClick: goToNotifications
                    }, {
                      default: _withCtx(() => [
                        _createVNode(AQIcon, {
                          size: "large",
                          name: "mdi-bell-outline"
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["content", "model-value"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_v_menu, {
                modelValue: userMenuOpen.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((userMenuOpen).value = $event)),
                "close-on-content-click": true,
                location: "bottom end",
                "offset-y": "",
                "content-class": "aq-menu-content"
              }, {
                activator: _withCtx(({ props: avatarProps }) => [
                  _createElementVNode("div", _mergeProps(avatarProps, {
                    class: "navbar__avatar-button d-flex align-center",
                    style: {"cursor":"pointer"}
                  }), [
                    _createElementVNode("span", _hoisted_7, _toDisplayString(clientInitial.value), 1)
                  ], 16)
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_v_list, {
                    dense: "",
                    class: "user-dropdown-menu"
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userMenuItems.value, (item) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: item.id
                        }, [
                          (item.isHeader)
                            ? (_openBlock(), _createBlock(_component_v_list_item, {
                                key: 0,
                                class: "user-dropdown-menu__header",
                                ripple: false,
                                value: item.id
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, { class: "font-weight-bold" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(item.dynamicText()), 1)
                                    ]),
                                    _: 2
                                  }, 1024)
                                ]),
                                _: 2
                              }, 1032, ["value"]))
                            : (item.isDivider)
                              ? (_openBlock(), _createBlock(_component_v_divider, {
                                  key: 'divider-' + item.id
                                }))
                              : (item.hasSubmenu)
                                ? (_openBlock(), _createBlock(_component_v_list_item, {
                                    key: 2,
                                    value: item.id,
                                    disabled: item.disabled,
                                    onClick: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"]))
                                  }, _createSlots({
                                    append: _withCtx(() => [
                                      _createVNode(AQIcon, {
                                        name: "mdi-chevron-right",
                                        size: "small"
                                      })
                                    ]),
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_list_item_title, null, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(item.text), 1)
                                        ]),
                                        _: 2
                                      }, 1024),
                                      _createVNode(_component_v_menu, {
                                        activator: "parent",
                                        location: "end",
                                        "close-on-content-click": true,
                                        "open-on-hover": "",
                                        "content-class": "aq-menu-content"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_list, { dense: "" }, {
                                            default: _withCtx(() => [
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.submenuItems, (subItem) => {
                                                return (_openBlock(), _createBlock(_component_v_list_item, {
                                                  key: subItem.id,
                                                  onClick: ($event: any) => (handleSubmenuItemClick(subItem))
                                                }, _createSlots({
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_list_item_title, null, {
                                                      default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(subItem.text), 1)
                                                      ]),
                                                      _: 2
                                                    }, 1024)
                                                  ]),
                                                  _: 2
                                                }, [
                                                  (subItem.icon)
                                                    ? {
                                                        name: "prepend",
                                                        fn: _withCtx(() => [
                                                          _createVNode(AQIcon, {
                                                            name: subItem.icon,
                                                            color: _unref(Colors).aquassistBlack
                                                          }, null, 8, ["name", "color"])
                                                        ]),
                                                        key: "0"
                                                      }
                                                    : undefined,
                                                  (subItem.isExternal)
                                                    ? {
                                                        name: "append",
                                                        fn: _withCtx(() => [
                                                          _createVNode(AQIcon, {
                                                            name: "mdi-open-in-new",
                                                            size: "small"
                                                          })
                                                        ]),
                                                        key: "1"
                                                      }
                                                    : undefined
                                                ]), 1032, ["onClick"]))
                                              }), 128))
                                            ]),
                                            _: 2
                                          }, 1024)
                                        ]),
                                        _: 2
                                      }, 1024)
                                    ]),
                                    _: 2
                                  }, [
                                    (item.icon)
                                      ? {
                                          name: "prepend",
                                          fn: _withCtx(() => [
                                            _createVNode(AQIcon, {
                                              name: item.icon,
                                              color: _unref(Colors).aquassistBlack
                                            }, null, 8, ["name", "color"])
                                          ]),
                                          key: "0"
                                        }
                                      : undefined
                                  ]), 1032, ["value", "disabled"]))
                                : (_openBlock(), _createBlock(_component_v_list_item, {
                                    key: 3,
                                    onClick: ($event: any) => (handleUserMenuItemClick(item.action)),
                                    disabled: item.disabled,
                                    value: item.id,
                                    class: _normalizeClass({ 'logout-item': item.id === 'logout' })
                                  }, _createSlots({
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_list_item_title, null, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(item.text), 1)
                                        ]),
                                        _: 2
                                      }, 1024)
                                    ]),
                                    _: 2
                                  }, [
                                    (item.icon)
                                      ? {
                                          name: "prepend",
                                          fn: _withCtx(() => [
                                            _createVNode(AQIcon, {
                                              name: item.icon,
                                              color: 
                                                item.id === 'logout'
                                                    ? _unref(Colors).aquassistRed
                                                    : _unref(Colors).aquassistBlack
                                            
                                            }, null, 8, ["name", "color"])
                                          ]),
                                          key: "0"
                                        }
                                      : undefined
                                  ]), 1032, ["onClick", "disabled", "value", "class"]))
                        ], 64))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ])
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(AQModal, {
      modelValue: isFeedbackModalOpen.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((isFeedbackModalOpen).value = $event)),
      title: _unref(t)('feedback.title'),
      onClose: _cache[9] || (_cache[9] = ($event: any) => (isFeedbackModalOpen.value = false)),
      onConfirm: handleConfirmModal,
      "is-confirm-disabled": isConfirmDisabled.value
    }, {
      default: _withCtx(() => [
        _createVNode(AQTextField, {
          name: "feedbackInput",
          modelValue: feedbackInput.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((feedbackInput).value = $event)),
          label: _unref(t)('forms.labels.subject'),
          required: ""
        }, null, 8, ["modelValue", "label"]),
        _createVNode(AQTextArea, {
          name: "feedbackMessage",
          modelValue: feedbackMessage.value,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((feedbackMessage).value = $event)),
          label: _unref(t)('forms.labels.message'),
          required: ""
        }, null, 8, ["modelValue", "label"])
      ]),
      _: 1
    }, 8, ["modelValue", "title", "is-confirm-disabled"])
  ], 64))
}
}

})